import React from 'react';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import SectionTitle from './shared/SectionTitle';

const ProcessContainer = styled(Box)({
    maxWidth: '1400px',
    margin: '0 auto',
});

const StepContainer = styled(Box)(({ theme }) => ({
    display: 'grid',
    gridTemplateColumns: '80px 1fr',
    gap: theme.spacing(3),
    marginBottom: theme.spacing(5),
    alignItems: 'start',
    [theme.breakpoints.down('md')]: {
        gridTemplateColumns: '60px 1fr',
        gap: theme.spacing(2),
        marginBottom: theme.spacing(4),
    },
}));

const StepNumber = styled(Box)(({ theme }) => ({
    width: 64,
    height: 64,
    borderRadius: '50%',
    backgroundColor: theme.palette.orange.main,
    color: '#FFFFFF',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    fontSize: '1.75rem',
    fontWeight: 700,
    flexShrink: 0,
    boxShadow: '0 4px 12px rgba(255, 107, 0, 0.3)',
    [theme.breakpoints.down('md')]: {
        width: 48,
        height: 48,
        fontSize: '1.5rem',
    },
}));

const StepContent = styled(Box)(({ theme }) => ({
    paddingTop: theme.spacing(1),
}));

const StepImage = styled('img')<{ $isSmarImage?: boolean }>(({ theme, $isSmarImage }) => ({
    width: '100%',
    maxWidth: $isSmarImage ? '506px' : '500px',
    height: 'auto',
    borderRadius: '8px',
    marginTop: theme.spacing(2),
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12)',
    border: '2px solid #1a3a5c',
    backgroundColor: '#ffffff',
    // SmarImg specific styles (padding only for the diagram)
    ...$isSmarImage && {
        padding: '12px',
    },
    [theme.breakpoints.down('md')]: {
        maxWidth: $isSmarImage ? '422px' : '100%',
    },
}));

interface Step {
    number: number;
    title: string;
    description: string;
    image?: string;
    imageAlt?: string;
}

const steps: Step[] = [
    {
        number: 1,
        title: 'Tell Us About Your Boat',
        description: 'We start with your exact boat model and your typical sailing style. Understanding your wind range, crew, and how you use your boat helps us design the perfect sail for your needs.',
    },
    {
        number: 2,
        title: '3D Sail Design in Professional Software',
        description: 'We use Smar Azure, professional-grade 3D sail design software, to model your sail. This allows us to adjust shape, twist, and ensure optimal performance and trim responsiveness for your specific boat.',
        image: '/images/SmarImg.png',
        imageAlt: 'Smar Azure 3D sail design software showing sail shape modeling',
    },
    {
        number: 3,
        title: 'Built in a Respected International Loft',
        description: 'Your sail is cut and assembled by skilled craftsmen using top-tier cloth and hardware. We inspect each sail for construction quality before shipping.',
        image: '/images/LoftFloorSail.jpg',
        imageAlt: 'Sail laid out on loft floor for cutting and assembly',
    },
    {
        number: 4,
        title: 'Quality Check & Shipment',
        description: 'Each sail is thoroughly inspected for construction quality before shipping. Typical delivery time is 5-7 weeks from order.',
        image: '/images/Construction1.jpg',
        imageAlt: 'Close-up of sail construction showing quality hardware and webbing',
    },
];

const ProcessSection: React.FC = () => {
    return (
        <ProcessContainer>
            <SectionTitle centered sx={{ mb: 5 }}>
                How Your Sail Is Designed & Built
            </SectionTitle>

            {steps.map((step) => (
                <StepContainer key={step.number}>
                    <StepNumber>
                        {step.number}
                    </StepNumber>
                    <StepContent>
                        <Typography 
                            variant="h5" 
                            gutterBottom
                            sx={{ 
                                fontWeight: 600,
                                fontSize: { xs: '1.25rem', sm: '1.5rem' },
                                color: 'primary.main',
                                mb: 1.5,
                            }}
                        >
                            {step.title}
                        </Typography>
                        <Typography 
                            variant="body1"
                            sx={{ 
                                fontSize: '1rem',
                                lineHeight: 1.7,
                                color: 'text.secondary',
                                mb: step.image ? 2 : 0,
                            }}
                        >
                            {step.description}
                        </Typography>
                        
                        {step.image && (
                            <StepImage
                                src={step.image}
                                alt={step.imageAlt}
                                loading="lazy"
                                $isSmarImage={step.image.includes('SmarImg')}
                            />
                        )}
                    </StepContent>
                </StepContainer>
            ))}
        </ProcessContainer>
    );
};

export default ProcessSection;
